/**
 * InsuranceDepositsRecords — every deposit a driver has put toward a premium, one row per payment.
 *
 * OFF `insurance_loan_deposits`, WHICH THIS PAGE HAD STOPPED READING. Both tabs read
 * `insurance_loan_repayments` — a live table holding one row that is not a deposit — after a
 * mechanical rename followed the string rather than the subject, so the table showed 1 row / GHS
 * 5,312.00 against an estate of 26 / GHS 75,052.46. `depositLedger.ts` holds every rule and is
 * tested; this file draws them.
 *
 * WHOLE-TABLE, CLIENT-SIDE, on `InsuranceLoansRecords`' precedent and for two of its own reasons:
 * the instrument is `SUM(this loan's deposits) >= amount − 0.005` (a correlated aggregate PostgREST
 * cannot express) and the "Toward premium" column is a running total over the loan's payments in
 * date order, which a page of ten rows cannot see. That also ends the two defects the old read
 * carried: a search box that had never filtered anything, and a `head: true` count sharing no
 * filter with the page query.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ListPagination } from '@/components/ListPagination';
import { useDebounce } from '@/hooks/useDebounce';
import { useServerPagination } from '@/hooks/useServerPagination';
import { cn } from '@/lib/utils';
import { formatBusinessDate, formatCurrency } from '@/lib/format';
import { Search } from 'lucide-react';
import {
    DEPOSIT_LEDGER_KEY,
    depositShare,
    fetchDepositLedger,
    matchesDepositSearch,
    sortDepositEntries,
} from './depositLedger';
import {
    PREMIUM_INSTRUMENT_LABELS,
    matchesInstrumentFilter,
    type PremiumInstrumentFilter,
} from './premiumLedger';

export default function InsuranceDepositsRecords() {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);
    const [instrumentFilter, setInstrumentFilter] = useState<PremiumInstrumentFilter>('all');

    const { data: deposits, isLoading, isFetching, error } = useQuery({
        queryKey: DEPOSIT_LEDGER_KEY,
        queryFn: fetchDepositLedger,
    });

    const rows = useMemo(
        () =>
            sortDepositEntries(
                (deposits ?? []).filter(
                    (row) =>
                        matchesInstrumentFilter(row, instrumentFilter) &&
                        matchesDepositSearch(row, debouncedSearch),
                ),
            ),
        [deposits, instrumentFilter, debouncedSearch],
    );

    const { currentPage, totalPages, totalItems, goToPage, resetPage, itemsPerPage } =
        useServerPagination({ totalCount: rows.length });

    // A narrowed list is a shorter list, so page 3 of the whole ledger is off the end of it.
    useEffect(() => { resetPage(); }, [debouncedSearch, instrumentFilter]);

    // THE PAGE NUMBER IS READ THROUGH A CLAMP RATHER THAN STORED CLAMPED. `useServerPagination`
    // keeps `currentPage` when `totalCount` shrinks (`useServerPagination.ts:9-17`), and a deposit
    // deleted or moved from the Insurance tab now refetches this ledger without a reload — so the
    // page an admin is standing on can lose its rows under them. Sliced off the stored number that
    // reads as an empty list on a full ledger, and the table below would state "No insurance
    // deposits found" over 25 payments. Clamped, it falls back to the last page that exists, and
    // the pager agrees with the slice because both are drawn from this number.
    const safePage = Math.min(currentPage, Math.max(1, totalPages));
    const from = (safePage - 1) * itemsPerPage;
    const page = rows.slice(from, from + itemsPerPage);
    // Two empty states, because they mean different things: nothing recorded at all, or nothing
    // left after the admin narrowed it. A ledger with no deposits in it reads as the former
    // whatever the controls say — a filter cannot be what emptied a list that was already empty.
    const narrowed =
        (deposits?.length ?? 0) > 0 &&
        (instrumentFilter !== 'all' || debouncedSearch.trim().length > 0);

    // A FAILED OR TRUNCATED READ DRAWS NO TABLE AT ALL. `fetchDepositLedger` throws when the
    // response came back shorter than the server's own count, and a truncated ledger does not
    // merely list fewer payments: the per-loan total is a group-by over exactly the rows that
    // arrived, so a cut through one loan's deposits relabels it. There is no list to narrow.
    if (error) {
        return (
            <Card className="border-0">
                <CardContent>
                    <p className="text-center text-destructive py-8">{error.message}</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="border-0">
            <CardContent className="space-y-4">
                {/* The controls row — search and the Instrument filter side by side, in the card
                    rather than the page header. */}
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search plate, vehicle, driver, policy or contract..."
                            className="pl-9"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <Select
                        value={instrumentFilter}
                        onValueChange={(value) => setInstrumentFilter(value as PremiumInstrumentFilter)}
                    >
                        <SelectTrigger className="w-[180px] shrink-0">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All instruments</SelectItem>
                            <SelectItem value="advance">Advances</SelectItem>
                            <SelectItem value="paid-in-full">Paid in full</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <ListPagination
                    currentPage={safePage}
                    totalPages={totalPages}
                    onPageChange={goToPage}
                    totalItems={totalItems}
                />

                {/* AN EMPTY LIST IS ONLY STATED ONCE THE READ HAS SETTLED. An invalidation from the
                    Insurance tab refetches this query in place; until it answers, an empty slice is
                    a read in flight rather than an empty ledger, and "No insurance deposits found"
                    on a page that has deposits is a worse answer than the skeleton. */}
                {isLoading || (isFetching && page.length === 0) ? (
                    <Skeleton className="h-48 w-full" />
                ) : page.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Contract</TableHead>
                                <TableHead>Driver</TableHead>
                                <TableHead>Vehicle</TableHead>
                                <TableHead>Policy year</TableHead>
                                <TableHead className="text-right">Premium</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                                <TableHead className="text-right">Toward premium</TableHead>
                                <TableHead>Instrument</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {page.map((row) => {
                                const share = depositShare(row);
                                return (
                                    <TableRow key={row.depositId}>
                                        {/* The payment's own business day — not the policy's start,
                                            and not when a clerk typed the link row. */}
                                        <TableCell>{formatBusinessDate(row.paidAt)}</TableCell>
                                        <TableCell>
                                            {row.contractId === null ? '-' : (
                                                <Link
                                                    to={`/dashboard/contracts/${row.contractId}`}
                                                    className="text-primary hover:underline"
                                                >
                                                    #{row.contractId}
                                                </Link>
                                            )}
                                        </TableCell>
                                        <TableCell>{row.driverName ?? '-'}</TableCell>
                                        <TableCell>
                                            {row.registration || row.make || row.model ? (
                                                <div>
                                                    <span className="font-medium">{row.registration || 'Unregistered'}</span>
                                                    <p className="text-xs text-muted-foreground">
                                                        {[row.make, row.model].filter(Boolean).join(' ')}
                                                    </p>
                                                </div>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {row.policyStart === null && row.policyEnd === null
                                                ? '-'
                                                : `${formatBusinessDate(row.policyStart)} – ${formatBusinessDate(row.policyEnd)}`}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {row.premium === null ? '-' : formatCurrency(row.premium)}
                                        </TableCell>
                                        {/* The row's subject: what the driver paid on this day. */}
                                        <TableCell className="text-right font-medium">
                                            {formatCurrency(row.amount)}
                                        </TableCell>
                                        {/* Σ of this loan's deposits up to and including this one,
                                            against the premium — so loan 294's two payments read,
                                            IN PAYMENT ORDER, "3,000 of 3,977 · 75%" (16 Jul) then
                                            "3,977 of 3,977 · 100%" (20 Jul). The table is sorted
                                            newest first, so the 100% row is the one ABOVE. Blank
                                            where there is no premium to be a share of; the
                                            percentage is rounded HERE, at display, and nowhere in
                                            the arithmetic. */}
                                        <TableCell className="text-right">
                                            {share === null || row.premium === null ? '' : (
                                                <div>
                                                    <span>{formatCurrency(row.runningTotal)}</span>
                                                    <p className="text-xs text-muted-foreground">
                                                        of {formatCurrency(row.premium)} · {Math.round(share * 100)}%
                                                    </p>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {/* The emerald/blue pair the contract's Insurance tab and
                                                the Premium Years table already use for these two
                                                shapes. The instrument is the LOAN's, so both of one
                                                loan's payments carry the same badge. */}
                                            <Badge
                                                variant="secondary"
                                                className={cn(
                                                    'font-normal text-xs px-2 py-0 h-5',
                                                    row.instrument === 'PAYMENT_RECORD'
                                                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
                                                        : 'bg-blue-50 text-blue-700 hover:bg-blue-50',
                                                )}
                                            >
                                                {PREMIUM_INSTRUMENT_LABELS[row.instrument]}
                                            </Badge>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="text-center text-muted-foreground py-8">
                        {narrowed ? 'No deposits match this filter' : 'No insurance deposits found'}
                    </p>
                )}
            </CardContent>
        </Card>
    );
}
